import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiMonitor, FiChevronDown } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';

const ToggleContainer = styled(motion.div)`
  position: relative;
  display: inline-block;
`;

const ToggleButton = styled(motion.button)<{ $isDarkMode: boolean }>`
  position: relative;
  width: 60px;
  height: 30px;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  border: none;
  border-radius: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  box-shadow: 0 4px 16px rgba(139, 92, 246, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
  }
`;

const ToggleHandle = styled(motion.div)`
  position: absolute;
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  left: 4px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: white;
  font-size: 12px;
  z-index: 1;
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: rgba(26, 26, 46, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 0.5rem;
  min-width: 140px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 1000;

  &[data-theme="light"] {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const MenuItem = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem;
  background: none;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &[data-theme="light"] {
    color: #0f172a;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  &[data-active="true"] {
    background: rgba(139, 92, 246, 0.2);
    color: #8b5cf6;
  }
`;


const SimpleThemeToggle: React.FC = () => {
  const { themeMode, isDarkMode, setThemeMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleThemeChange = (mode: 'system' | 'light' | 'dark') => {
    setThemeMode(mode);
    setIsOpen(false);
  };

  const getCurrentIcon = () => {
    switch (themeMode) {
      case 'light':
        return <FiSun size={14} />;
      case 'dark':
        return <FiMoon size={14} />;
      case 'system':
        return <FiMonitor size={14} />;
      default:
        return <FiMonitor size={14} />;
    }
  };

  const getHandlePosition = () => {
    switch (themeMode) {
      case 'light':
        return 0;
      case 'dark':
        return 30;
      case 'system':
        return 15;
      default:
        return 15;
    }
  };

  return (
    <ToggleContainer ref={containerRef}>
      <ToggleButton
        $isDarkMode={isDarkMode}
        onClick={() => setIsOpen(!isOpen)}
        whileTap={{ scale: 0.95 }}
        initial={false}
      >
        <IconContainer>
          {getCurrentIcon()}
        </IconContainer>
        <IconContainer>
          <FiChevronDown size={12} />
        </IconContainer>
        <ToggleHandle
          animate={{
            x: getHandlePosition(),
          }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 30
          }}
        />
      </ToggleButton>

      <AnimatePresence>
        {isOpen && (
          <DropdownMenu
            as={motion.div}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            data-theme={isDarkMode ? 'dark' : 'light'}
          >
            <MenuItem
              onClick={() => handleThemeChange('system')}
              data-active={themeMode === 'system'}
              data-theme={isDarkMode ? 'dark' : 'light'}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiMonitor size={16} />
              System
            </MenuItem>
            <MenuItem
              onClick={() => handleThemeChange('light')}
              data-active={themeMode === 'light'}
              data-theme={isDarkMode ? 'dark' : 'light'}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiSun size={16} />
              Light
            </MenuItem>
            <MenuItem
              onClick={() => handleThemeChange('dark')}
              data-active={themeMode === 'dark'}
              data-theme={isDarkMode ? 'dark' : 'light'}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiMoon size={16} />
              Dark
            </MenuItem>
          </DropdownMenu>
        )}
      </AnimatePresence>
    </ToggleContainer>
  );
};

export default SimpleThemeToggle;
